import { Button, PhoneFilledIcon, Tooltip } from '@calizahq/react-components-v2'
import { WorkspaceView } from '@sales-app/context/workspace/workspace.type'
import { getWorkspacePath } from '@sales-app/context/workspace/workspace.util'
import { PotentialBooking } from '@sales-app/graphql/queries/potentialBooking/potentialBookings.queries'
import { Agent } from '@sales-app/types/Agent'
import { BookablePersonType } from '@sales-app/types/BookablePersonType'
import { getPotentialBookingStatusClassNames } from '@sales-app/views/Workspace/PersonSummary/PersonSummary.data'
import { ColumnDef } from '@tanstack/react-table'
import { formatInTimeZone } from 'date-fns-tz'
import React from 'react'
import { useNavigate } from 'react-router-dom'

interface PotentialBookingRow {
  agent: Agent | null
  bookablePersonType: BookablePersonType
  createdAt: string
  email: string
  fullName: string
  id: number
  market: string
  moveIn: string | null
  phone: string
  potentialBooking: PotentialBooking
  source: string
  status: string
}

export const PotentialBookingsColumns: ColumnDef<PotentialBookingRow, any>[] = [
  {
    header: 'Name',
    accessorKey: 'fullName',
    minSize: 100,
    enableSorting: false,
  },
  {
    header: 'Phone',
    accessorKey: 'phone',
    minSize: 100,
    enableSorting: false,
    cell: ({ getValue }) => {
      const value = getValue() as string
      return (
        <div className="flex underline mr-8">
          {value ? (
            <a href={`dialpad://${value}`} rel="noreferrer" target="_blank">
              <div className="flex flex-row">
                <div>
                  <Tooltip text="Call with Dialpad">
                    <PhoneFilledIcon className="w-6 mr-2" height={28} width={28} />
                  </Tooltip>
                </div>
                <div className="mt-1">{value}</div>
              </div>
            </a>
          ) : null}
        </div>
      )
    },
  },
  {
    header: 'Market',
    accessorKey: 'market',
    size: 50,
    enableSorting: false,
  },
  {
    header: 'Status',
    accessorKey: 'status',
    enableSorting: false,
    size: 100,
    cell: ({ getValue }) => {
      const value = getValue() as string
      const className = getPotentialBookingStatusClassNames(value)
      return (
        <div className="flex w-full items-center justify-center">
          <div
            className={`w-fit-content cursor-pointer rounded-lg px-3 py-2 capitalize leading-140
               ${className}`}
          >
            {value}
          </div>
        </div>
      )
    },
  },
  {
    header: 'Agent',
    accessorFn: (row) => row.agent?.name,
    id: 'agent.name',
    enableSorting: false,
    size: 50,
  },
  {
    header: 'Source',
    accessorKey: 'source',
    enableSorting: false,
    size: 50,
  },
  {
    header: 'Created',
    accessorKey: 'createdAt',
    size: 100,
    cell: function Cell({ getValue }: any) {
      const value: string = getValue() ?? ''
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
      try {
        const time = formatInTimeZone(value, timezone, 'MMM dd, yyyy hh:mm aa')
        return time
      } catch (e) {
        return 'Invalid  Date'
      }
    },
  },
  {
    header: 'Move In',
    accessorKey: 'moveIn',
    size: 100,
    cell: function Cell({ getValue }: any) {
      const value: string = getValue() ?? ''
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
      try {
        const time = formatInTimeZone(value, timezone, 'MMM d, yyyy')
        return time
      } catch (e) {
        return '-'
      }
    },
  },
  {
    header: 'Type',
    enableSorting: false,
    accessorKey: 'bookablePersonType',
    size: 50,
  },
  {
    id: 'actions',
    accessorKey: 'id',
    enableSorting: false,
    size: 120,
    maxSize: 120,
    cell: function Cell({ row }: { row: { original: PotentialBookingRow } }) {
      const navigate = useNavigate()
      const { id } = row.original
      const bookablePerson = row.original.potentialBooking.bookablePerson
      const userId = bookablePerson?.bookablePersonId
      const openPotentialBooking = () => {
        if (userId) {
          const path = getWorkspacePath({
            view: WorkspaceView.POTENTIALBOOKING,
            type: bookablePerson.bookablePersonType,
            id: userId,
            potentialBookingId: id,
          })

          navigate(path)
        }
      }
      return (
        <div className="ml-auto flex items-center justify-center">
          <Button className=" bg-text-semantic-success" onClick={openPotentialBooking} size="small">
            View
          </Button>
        </div>
      )
    },
  },
]

export const getStatusTagClassName = (): string => {
  return 'bg-emphasis text-black text-p-sm'
}

export const transformedPotentialBooking = (potentialBookings: PotentialBooking[]): PotentialBookingRow[] => {
  return potentialBookings.map((p) => {
    const bookablePerson = p.bookablePerson

    return {
      status: p.potentialBookingStatus,
      id: p.id,
      market: p.displayCity?.name,
      moveIn: p.beginningMoveInDate,
      createdAt: p.createdAt,
      potentialBooking: p,
      fullName: bookablePerson?.fullName || '',
      email: bookablePerson?.email || '',
      phone: bookablePerson?.phone || '',
      bookablePersonType: p.bookablePersonType,
      agent: p.agent,
      source: p.potentialBookingSource,
    }
  })
}
